'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';

import { useRouteChangeListener, useRouter } from '@northladder/i18n';

type TNavFunc = 'push' | 'replace';
type TSessionType = 'valid' | 'invalid';

/**
 * This adds some level of safe routing to avoid pushing multiple routes when
 * by use effect or slow network when the app is still processing other routes,
 * such as when the user clicks multiple times or when timers are still running.
 * - Avoids ..."Error: Abort fetching component for route"
 */
const useSafeRoute = () => {
  const router = useRouter();
  const [isRouting, setIsRouting] = useState(false);

  const handleRouteChange = () => {
    setIsRouting(false);
  };

  /**
   * Wrapper around the Nextjs `router` to navigate to prevent repetition.
   * @param path - The path to navigate to
   * @param type - Whether to `replace` the current route or `push` a new one.
   * @returns
   */
  const safeNavigateToPath = (path: string, type: TNavFunc) => {
    if (isRouting) return;
    setIsRouting(true);

    const navFunc = type === 'push' ? router.push : router.replace;

    navFunc(path);
  };

  useRouteChangeListener(handleRouteChange);

  return {
    router,
    isRouting,
    safeNavigateToPath,
  };
};

/**
 * Wrapper around the Next.js router to navigate to prevent repetition.
 *
 * @param baseUrl - The base path to which the route should navigate to.
 */
export const useNavRouter = () => {
  const { router, isRouting, safeNavigateToPath } = useSafeRoute();
  const searchParams = useSearchParams();

  const evaluationId = (searchParams?.get('evaluationId') || '') as string;
  const platform = (searchParams?.get('platform') || '') as string;
  const sessionType = (searchParams?.get('sessionType') ||
    'valid') as TSessionType;

  /**
   * Gets the full URL to which the route should navigate to.
   *
   * @param basePath - The base path to which the route should navigate to.
   */
  const getRouteURL = (basePath: string, exact?: boolean) => {
    if (exact) return basePath || '/';

    return `${basePath}?platform=${platform}&evaluationId=${evaluationId}`;
  };

  /**
   * Navigates the page to a provided route.
   *
   * @param path - The path to which the route should navigate to.
   * @param type - Whether to `replace` the current route or `push` a new one.
   */
  const navigateToRoute = (path: string, type: TNavFunc = 'push') => {
    safeNavigateToPath(getRouteURL(path || ''), type);
  };

  /**
   * Goes back to the root path.
   * @param type - Whether to `replace` the current route or `push` a new one.
   * @param queryParams - The route query params to be added to the session.
   */
  const resetNavPath = (type?: TNavFunc, queryParams?: string) => {
    const navType = type || 'replace';

    if (queryParams) {
      safeNavigateToPath(getRouteURL(`/${queryParams}`, true), navType);
    } else {
      safeNavigateToPath(getRouteURL('/', true), navType);
    }
  };

  const prefetchRoute = (path: string) => {
    router.prefetch(getRouteURL(path || ''));
  };

  return {
    getRouteURL,
    evaluationId,
    isRouting,
    navigateToRoute,
    platform,
    prefetchRoute,
    resetNavPath,
    /**
     * Whether the current session is valid or invalid. Mostly set via query params.
     * When the API evaluation ID validation fails, e.g the `sound-performance-evaluate`.
     */
    sessionType,
  };
};
